import React, { useState } from 'react';
import InteractiveText from './InteractiveText';
import { getSpanishMeaning } from '../utils/dictionaryHelper';

// 📚 BASE DE DATOS COMPLETA DE VOCABULARIO (MÍNIMO 10 PALABRAS MÁS USADAS POR UNIDAD)
export const UNIT_VOCABULARY_DATABASE = {
  '6': {
    1: {
      unitName: 'Unidad 1: Saludos, Datos Personales y Alfabeto',
      categories: [
        {
          categoryName: '👋 Saludos, Despedidas y Expresiones de Cortesía',
          words: [
            {
              term: 'Good morning',
              spanish: 'Buenos días',
              examples: [
                { en: 'Good morning! My name is Carlos.', es: '¡Buenos días! Mi nombre es Carlos.' },
                { en: 'Students greet the teacher with "Good morning".', es: 'Los estudiantes saludan al profesor con "Buenos días".' }
              ],
              tip: '🗣️ Pronunciación: "Gud mórning". Se usa desde las 6:00 AM hasta las 12:00 PM.'
            },
            {
              term: 'Nice to meet you',
              spanish: 'Mucho gusto en conocerte',
              examples: [
                { en: 'Hello Ana! Nice to meet you.', es: '¡Hola Ana! Gusto en conocerte.' },
                { en: 'Nice to meet you too, David.', es: 'Gusto en conocerte a ti también, David.' }
              ],
              tip: '💡 Tip ICFES: La respuesta cortés siempre incluye "too" al final: "Nice to meet you too".'
            },
            {
              term: 'Please and Thank you',
              spanish: 'Por favor y Gracias',
              examples: [
                { en: 'Can you help me, please?', es: '¿Puedes ayudarme, por favor?' },
                { en: 'Thank you very much for your explanation.', es: 'Muchas gracias por tu explicación.' }
              ],
              tip: '🗣️ Pronunciación: "Plis" y "Tenk iú". Expresiones básicas de cortesía.'
            },
            {
              term: 'Goodbye / See you',
              spanish: 'Hasta luego / Nos vemos',
              examples: [
                { en: 'Goodbye! See you tomorrow.', es: '¡Hasta luego! Nos vemos mañana.' },
                { en: 'See you next class in the English lab.', es: 'Nos vemos la próxima clase en el laboratorio de inglés.' }
              ],
              tip: '🗣️ Despedida rápida: "See you later" significa nos vemos más tarde.'
            },
            {
              term: 'How are you?',
              spanish: '¿Cómo estás?',
              examples: [
                { en: 'Hello Sofia! How are you today?', es: '¡Hola Sofia! ¿Cómo estás hoy?' },
                { en: 'I am very well, thank you!', es: '¡Estoy muy bien, gracias!' }
              ],
              tip: '💡 Respuesta común: "I am fine, thank you" o "Very well".'
            }
          ]
        },
        {
          categoryName: '📝 Datos Personales y Números Básicos',
          words: [
            {
              term: 'First name',
              spanish: 'Primer nombre',
              examples: [
                { en: 'My first name is Mateo.', es: 'Mi primer nombre es Mateo.' },
                { en: 'Write your first name on the test sheet.', es: 'Escribe tu primer nombre en la hoja de examen.' }
              ],
              tip: '💡 Diferencia: "First name" = nombre / "Last name" = apellido.'
            },
            {
              term: 'Last name',
              spanish: 'Apellido',
              examples: [
                { en: 'My last name is Gomez.', es: 'Mi apellido es Gómez.' },
                { en: 'How do you spell your last name?', es: '¿Cómo deletreas tu apellido?' }
              ],
              tip: '⚠️ Error común: "Last name" o "Surname" es el apellido del estudiante.'
            },
            {
              term: 'Fifteen (15)',
              spanish: 'Quince (15)',
              examples: [
                { en: 'The number after fourteen is fifteen.', es: 'El número después de catorce es quince.' },
                { en: 'There are fifteen students in the room.', es: 'Hay quince estudiantes en el salón.' }
              ],
              tip: '⚠️ Atención en ICFES: No confundir "fifteen" (15) con "fifty" (50).'
            },
            {
              term: 'Spell',
              spanish: 'Deletrear',
              examples: [
                { en: 'How do you spell "BOOK" in English?', es: '¿Cómo deletreas "BOOK" en inglés?' },
                { en: 'I can spell my name: M-A-T-E-O.', es: 'Puedo deletrear mi nombre: M-A-T-E-O.' }
              ],
              tip: '🗣️ Pregunta clave: "How do you spell...?" exige deletrear letra por letra.'
            },
            {
              term: 'Welcome',
              spanish: 'Bienvenido / Bienvenida',
              examples: [
                { en: 'Welcome to IE EOH school!', es: '¡Bienvenidos al colegio IE EOH!' },
                { en: 'You are welcome to join the English club.', es: 'Eres bienvenido a unirte al club de inglés.' }
              ],
              tip: '📍 Frecuente en avisos: "Welcome visitors" (Bienvenidos visitantes).'
            }
          ]
        }
      ]
    },
    2: {
      unitName: 'Unidad 2: La Familia y Partes de la Casa',
      categories: [
        {
          categoryName: '👨‍👩‍👧‍👦 Miembros de la Familia',
          words: [
            {
              term: 'Mother / Mom',
              spanish: 'Madre / Mamá',
              examples: [
                { en: 'My mother is in the living room.', es: 'Mi madre está en la sala de estar.' },
                { en: 'Mom cooks delicious food for the family.', es: 'Mamá cocina deliciosa comida para la familia.' }
              ],
              tip: '🗣️ Pronunciación: "Máder". Expresión afectuosa: "Mom".'
            },
            {
              term: 'Father / Dad',
              spanish: 'Padre / Papá',
              examples: [
                { en: 'His father works in Medellín.', es: 'Su padre trabaja en Medellín.' },
                { en: 'My dad helps me with my homework.', es: 'Mi papá me ayuda con mi tarea.' }
              ],
              tip: '🗣️ Pronunciación: "Fáder". Expresión afectuosa: "Dad".'
            },
            {
              term: 'Grandfather',
              spanish: 'Abuelo',
              examples: [
                { en: 'My grandfather lives with us.', es: 'Mi abuelo vive con nosotros.' },
                { en: 'David loves spending time with his grandfather.', es: 'A David le encanta pasar tiempo con su abuelo.' }
              ],
              tip: '🗣️ Pronunciación: "Gránd-fáder". Cariñosamente: "Grandpa".'
            },
            {
              term: 'Cousin',
              spanish: 'Primo / Prima',
              examples: [
                { en: 'Sofia is my cousin.', es: 'Sofia es mi prima.' },
                { en: 'My cousins play football on weekends.', es: 'Mis primos juegan fútbol los fines de semana.' }
              ],
              tip: '💡 Tip gramatical: Sirve tanto para hombre (primo) como para mujer (prima).'
            },
            {
              term: 'His / Her',
              spanish: 'Su (de él) / Su (de ella)',
              examples: [
                { en: 'David lives with his parents.', es: 'David vive con sus padres (de él).' },
                { en: 'Lucia loves her family.', es: 'Lucia ama a su familia (de ella).' }
              ],
              tip: '⚠️ Error muy común: "His" es para hombres y "Her" para mujeres.'
            }
          ]
        },
        {
          categoryName: '🏠 Habitaciones y Entornos de la Casa',
          words: [
            {
              term: 'Kitchen',
              spanish: 'Cocina',
              examples: [
                { en: 'My mother is in the kitchen.', es: 'Mi madre está en la cocina.' },
                { en: 'Please keep the kitchen clean.', es: 'Por favor mantén la cocina limpia.' }
              ],
              tip: '⚠️ Error común: "Kitchen" es cocina / "Chicken" es pollo.'
            },
            {
              term: 'Bedroom',
              spanish: 'Dormitorio / Habitación',
              examples: [
                { en: 'My bed is in my bedroom.', es: 'Mi cama está en mi dormitorio.' },
                { en: 'Lucia is reading in her bedroom.', es: 'Lucia está leyendo en su habitación.' }
              ],
              tip: '🗣️ Pronunciación: "Béd-rum". Composición: Bed (cama) + Room (cuarto).'
            },
            {
              term: 'Living room',
              spanish: 'Sala de estar / Salón',
              examples: [
                { en: 'We watch TV in the living room.', es: 'Vemos televisión en la sala de estar.' },
                { en: 'The sofa is in the living room.', es: 'El sofá está en la sala.' }
              ],
              tip: '🗣️ Pronunciación: "Líving rum". Zona común de la casa.'
            },
            {
              term: 'Bathroom',
              spanish: 'Baño',
              examples: [
                { en: 'Wash your hands in the bathroom.', es: 'Lávate las manos en el baño.' },
                { en: 'The bathroom is clean.', es: 'El baño está limpio.' }
              ],
              tip: '🗣️ Pronunciación: "Báth-rum". Bath (baño) + Room (cuarto).'
            },
            {
              term: 'Garden',
              spanish: 'Jardín / Patio verde',
              examples: [
                { en: 'Beware of the dog in the garden.', es: 'Cuidado con el perro en el jardín.' },
                { en: 'There are flowers in the garden.', es: 'Hay flores en el jardín.' }
              ],
              tip: '📍 Frecuente en avisos: "Beware of the dog in the garden".'
            }
          ]
        }
      ]
    },
    3: {
      unitName: 'Unidad 3: Rutinas Diarias y Horarios',
      categories: [
        {
          categoryName: '⏰ Verbos de Hábitos Cotidianos',
          words: [
            {
              term: 'Wake up',
              spanish: 'Despertarse',
              examples: [
                { en: 'I wake up at 6:00 AM every morning.', es: 'Me despierto a las 6:00 AM todas las mañanas.' },
                { en: 'Mateo wakes up early for school.', es: 'Mateo se despierta temprano para el colegio.' }
              ],
              tip: '💡 En 3ª persona (He/She) agregamos "s": "He wakes up".'
            },
            {
              term: 'Study English',
              spanish: 'Estudiar inglés',
              examples: [
                { en: 'We study English in the morning.', es: 'Estudiamos inglés en la mañana.' },
                { en: 'She studies English at IE EOH.', es: 'Ella estudia inglés en la IE EOH.' }
              ],
              tip: '⚠️ Ortografía: Study cambia a "studies" cuando el sujeto es He o She.'
            },
            {
              term: 'Brush teeth',
              spanish: 'Cepillarse los dientes',
              examples: [
                { en: 'I brush my teeth after breakfast.', es: 'Me cepillo los dientes después del desayuno.' },
                { en: 'Brush your teeth three times a day.', es: 'Cepíllate los dientes tres veces al día.' }
              ],
              tip: '🗣️ Pronunciación: "Brash tiith". Teeth es el plural de tooth.'
            },
            {
              term: 'Have breakfast',
              spanish: 'Desayunar',
              examples: [
                { en: 'We have breakfast at 7:00 AM.', es: 'Desayunamos a las 7:00 AM.' },
                { en: 'She has breakfast with her family.', es: 'Ella desayuna con su familia.' }
              ],
              tip: '💡 En inglés se usa el verbo "have": Have breakfast / Have lunch.'
            },
            {
              term: 'Go to bed',
              spanish: 'Ir a dormir / Acostarse',
              examples: [
                { en: 'I go to bed at 9:30 PM.', es: 'Me voy a dormir a las 9:30 PM.' },
                { en: 'Children go to bed early.', es: 'Los niños se acuestan temprano.' }
              ],
              tip: '🗣️ Expresión idiomática: "Go to sleep" o "Go to bed".'
            }
          ]
        },
        {
          categoryName: '📅 Expresiones de Tiempo y Frecuencia',
          words: [
            {
              term: 'Every day',
              spanish: 'Todos los días / Diario',
              examples: [
                { en: 'I practice English every day.', es: 'Practico inglés todos los días.' },
                { en: 'Students come to school every day.', es: 'Los estudiantes vienen al colegio todos los días.' }
              ],
              tip: '💡 Marcador de frecuencia del Presente Simple.'
            },
            {
              term: 'In the afternoon',
              spanish: 'En la tarde',
              examples: [
                { en: 'We do homework in the afternoon.', es: 'Hacemos la tarea en la tarde.' },
                { en: 'The class meets in the afternoon.', es: 'La clase se reúne en la tarde.' }
              ],
              tip: '🗣️ Pronunciación: "In di áfter-nun".'
            },
            {
              term: 'On Fridays',
              spanish: 'Los viernes',
              examples: [
                { en: 'We have physical education on Fridays.', es: 'Tenemos educación física los viernes.' },
                { en: 'The club meets on Fridays.', es: 'El club se reúne los viernes.' }
              ],
              tip: '💡 Preposición para días de la semana: Siempre usas ON (On Fridays).'
            },
            {
              term: 'Always',
              spanish: 'Siempre',
              examples: [
                { en: 'I always do my homework on time.', es: 'Siempre hago mi tarea a tiempo.' },
                { en: 'She is always polite with classmates.', es: 'Ella siempre es educada con sus compañeros.' }
              ],
              tip: '💡 Posición: Se coloca ANTES del verbo principal o DESPUÉS del verbo To Be.'
            },
            {
              term: 'Usually',
              spanish: 'Usualmente / Por lo general',
              examples: [
                { en: 'We usually eat lunch at 12:30 PM.', es: 'Usualmente almorzamos a las 12:30 PM.' },
                { en: 'David usually studies in the library.', es: 'David usualmente estudia en la biblioteca.' }
              ],
              tip: '🗣️ Pronunciación: "Yú-zhua-li". Frecuencia alta (80%).'
            }
          ]
        }
      ]
    },
    4: {
      unitName: 'Unidad 4: Materias Escolares y Habilidades (CAN)',
      categories: [
        {
          categoryName: '🏫 Entorno Escolar y Objetos de Clase',
          words: [
            {
              term: 'English Lab',
              spanish: 'Laboratorio de Inglés',
              examples: [
                { en: 'Welcome to the IE EOH English Lab!', es: '¡Bienvenidos al Laboratorio de Inglés de la IE EOH!' },
                { en: 'We practice reading in the English Lab.', es: 'Practicamos lectura en el Laboratorio de Inglés.' }
              ],
              tip: '🗣️ Pronunciación: "Ínglish Láb". Aula temática escolar.'
            },
            {
              term: 'Classroom',
              spanish: 'Salón de clases / Aula',
              examples: [
                { en: 'Please be quiet, class in progress.', es: 'Por favor silencio, clase en curso.' },
                { en: 'Our classroom is clean and bright.', es: 'Nuestro salón de clases está limpio y bien iluminado.' }
              ],
              tip: '📍 Aviso común ICFES: "Classroom in progress".'
            },
            {
              term: 'Dictionary',
              spanish: 'Diccionario',
              examples: [
                { en: 'Use an English dictionary for new words.', es: 'Usa un diccionario de inglés para palabras nuevas.' },
                { en: 'Could you lend me your dictionary?', es: '¿Podrías prestarme tu diccionario?' }
              ],
              tip: '🗣️ Pronunciación: "Dík-shon-ari".'
            },
            {
              term: 'Teacher',
              spanish: 'Profesor / Docente',
              examples: [
                { en: 'Mr. Didier is our English teacher.', es: 'El Prof. Didier es nuestro profesor de inglés.' },
                { en: 'Ask the teacher if you have questions.', es: 'Pregunta al profesor si tienes dudas.' }
              ],
              tip: '🗣️ Pronunciación: "Tí-cher".'
            },
            {
              term: 'Notebook',
              spanish: 'Cuaderno de apuntes',
              examples: [
                { en: 'Write the grammar rules in your notebook.', es: 'Escribe las reglas gramaticales en tu cuaderno.' },
                { en: 'Open your notebook to page 10.', es: 'Abre tu cuaderno en la página 10.' }
              ],
              tip: '🗣️ Note (nota) + Book (libro) = Cuaderno.'
            }
          ]
        },
        {
          categoryName: '⚡ Habilidades y Verbos de Acción (CAN)',
          words: [
            {
              term: 'Can (Poder)',
              spanish: 'Poder (habilidad o permiso)',
              examples: [
                { en: 'I can speak basic English.', es: 'Puedo hablar inglés básico.' },
                { en: 'Can you solve this exercise?', es: '¿Puedes resolver este ejercicio?' }
              ],
              tip: '⚠️ Regla de Oro: Después de CAN el verbo va en infinitivo SIN "to". Ej: "I can run".'
            },
            {
              term: 'Cannot / Can\'t',
              spanish: 'No poder / Imposibilidad',
              examples: [
                { en: 'She can\'t use her phone in class.', es: 'Ella no puede usar su teléfono en clase.' },
                { en: 'We cannot enter the exam room late.', es: 'No podemos entrar tarde al salón de examen.' }
              ],
              tip: '🗣️ Forma contraída: "Can\'t" se pronuncia "kant".'
            },
            {
              term: 'Listen carefully',
              spanish: 'Escuchar atentamente',
              examples: [
                { en: 'Listen carefully to the teacher.', es: 'Escucha atentamente al profesor.' },
                { en: 'Listen carefully before selecting an option.', es: 'Escucha con atención antes de elegir una opción.' }
              ],
              tip: '🗣️ Pronunciación: En "listen" la letra T no suena: "Lísen".'
            },
            {
              term: 'Read text',
              spanish: 'Leer el texto',
              examples: [
                { en: 'Read the text and answer the questions.', es: 'Lee el texto y responde las preguntas.' },
                { en: 'Critical reading helps you in ICFES.', es: 'La lectura crítica te ayuda en el ICFES.' }
              ],
              tip: '🗣️ Pronunciación: "Riid text". Instrucción básica de exámenes.'
            },
            {
              term: 'Write sentences',
              spanish: 'Escribir oraciones',
              examples: [
                { en: 'Write 5 sentences in Present Simple.', es: 'Escribe 5 oraciones en Presente Simple.' },
                { en: 'Practice writing sentences daily.', es: 'Practica escribir oraciones a diario.' }
              ],
              tip: '🗣️ Pronunciación: "Rait séntenses". La W en write no suena.'
            }
          ]
        }
      ]
    },
    5: {
      unitName: 'Unidad 5: Pasatiempos, Gustos y Preposiciones',
      categories: [
        {
          categoryName: '⚽ Pasatiempos y Preferencias',
          words: [
            {
              term: 'Reading books',
              spanish: 'Leer libros',
              examples: [
                { en: 'I like reading books in English.', es: 'Me gusta leer libros en inglés.' },
                { en: 'Reading books expands your vocabulary.', es: 'Leer libros expande tu vocabulario.' }
              ],
              tip: '💡 Después de "like" usamos el verbo con -ING: "like reading".'
            },
            {
              term: 'Playing football',
              spanish: 'Jugar fútbol',
              examples: [
                { en: 'Boys play football in the yard.', es: 'Los niños juegan fútbol en el patio.' },
                { en: 'He enjoys playing football on Saturdays.', es: 'Él disfruta jugar fútbol los sábados.' }
              ],
              tip: '🗣️ En inglés británico/colombiano es football; en EE.UU. soccer.'
            },
            {
              term: 'Listening to music',
              spanish: 'Escuchar música',
              examples: [
                { en: 'Sofia loves listening to music.', es: 'A Sofia le encanta escuchar música.' },
                { en: 'Listening to music helps you learn languages.', es: 'Escuchar música te ayuda a aprender idiomas.' }
              ],
              tip: '⚠️ Recuerda incluir "to": "listen TO music" (NO: listen music).'
            },
            {
              term: 'Swimming',
              spanish: 'Nadar / Natación',
              examples: [
                { en: 'We go swimming in the pool.', es: 'Vamos a nadar en la piscina.' },
                { en: 'Swimming is a great exercise.', es: 'Nadar es un gran ejercicio.' }
              ],
              tip: '🗣️ Se escribe con doble m al agregar -ing: swim -> swimming.'
            },
            {
              term: 'Like / Love',
              spanish: 'Gustar / Encantar',
              examples: [
                { en: 'I like English class.', es: 'Me gusta la clase de inglés.' },
                { en: 'She loves her school.', es: 'Ella ama su colegio.' }
              ],
              tip: '💡 Love expresa mayor preferencia o entusiasmo que Like.'
            }
          ]
        },
        {
          categoryName: '📍 Preposiciones de Ubicación (IN, ON, AT, UNDER)',
          words: [
            {
              term: 'On the table',
              spanish: 'Sobre la mesa',
              examples: [
                { en: 'The dictionary is on the table.', spanish: 'El diccionario está sobre la mesa.' },
                { en: 'Put your pencil on the table.', es: 'Pon tu lápiz sobre la mesa.' }
              ],
              tip: '💡 ON se usa cuando el objeto toca la superficie de algo.'
            },
            {
              term: 'In the box',
              spanish: 'Dentro de la caja',
              examples: [
                { en: 'The toys are in the box.', es: 'Los juguetes están dentro de la caja.' },
                { en: 'Keep your materials in the box.', es: 'Guarda tus materiales en la caja.' }
              ],
              tip: '💡 IN indica que el objeto está en el interior de un espacio.'
            },
            {
              term: 'Under the chair',
              spanish: 'Debajo de la silla',
              examples: [
                { en: 'The cat is sleeping under the chair.', es: 'El gato está durmiendo debajo de la silla.' },
                { en: 'Your bag is under the chair.', es: 'Tu maleta está debajo de la silla.' }
              ],
              tip: '🗣️ Pronunciación: "Ánder da cher".'
            },
            {
              term: 'At school',
              spanish: 'En el colegio',
              examples: [
                { en: 'We are at school right now.', es: 'Estamos en el colegio en este momento.' },
                { en: 'She learns many things at school.', es: 'Ella aprende muchas cosas en el colegio.' }
              ],
              tip: '💡 Preposición AT: Se usa para ubicaciones institucionales o puntos específicos.'
            },
            {
              term: 'In front of',
              spanish: 'Enfrente de / Delante de',
              examples: [
                { en: 'The teacher stands in front of the board.', es: 'El profesor se para enfrente del tablero.' },
                { en: 'Wait for me in front of the gate.', es: 'Espérame enfrente de la portería.' }
              ],
              tip: '🗣️ Pronunciación: "In frant of". Indica posición frontal.'
            }
          ]
        }
      ]
    }
  },
  '7': {
      1: {
        unitName: 'Unidad 1: Presente Continuo y Acciones en Progreso',
        categories: [
          {
            categoryName: '⚡ Verbos de Acción en Progreso (-ING)',
            words: [
              {
                term: 'Practicing',
                spanish: 'Practicando',
                examples: [
                  { en: 'Students are practicing English right now.', es: 'Los estudiantes están practicando inglés en este momento.' },
                  { en: 'I am practicing ICFES reading comprehension.', es: 'Estoy practicando comprensión de lectura ICFES.' }
                ],
                tip: '🗣️ Pronunciación: "Práktising". La terminación -ING indica progreso.'
              },
              {
                term: 'Writing',
                spanish: 'Escribiendo',
                examples: [
                  { en: 'She is writing a story in her notebook.', es: 'Ella está escribiendo una historia en su cuaderno.' },
                  { en: 'We are writing sentences in Present Continuous.', es: 'Estamos escribiendo oraciones en Presente Continuo.' }
                ],
                tip: '⚠️ Ortografía: Write elimina la e final al agregar -ing: write -> writing.'
              },
              {
                term: 'Reading',
                spanish: 'Leyendo',
                examples: [
                  { en: 'He is reading an interesting article.', es: 'Él está leyendo un artículo interesante.' },
                  { en: 'They are reading the signs on the wall.', es: 'Ellos están leyendo los avisos en la pared.' }
                ],
                tip: '🗣️ Pronunciación: "Riiding".'
              },
              {
                term: 'Listening to',
                spanish: 'Escuchando a',
                examples: [
                  { en: 'Students are listening to the explanation.', es: 'Los estudiantes están escuchando la explicación.' },
                  { en: 'I am listening to an audio file.', es: 'Estoy escuchando un archivo de audio.' }
                ],
                tip: '⚠️ Recuerda incluir "to": "listening TO".'
              },
              {
                term: 'Explaining',
                spanish: 'Explicando',
                examples: [
                  { en: 'Teacher Didier is explaining the grammar rule.', es: 'El profesor Didier está explicando la regla gramatical.' },
                  { en: 'She is explaining how to solve the exercise.', es: 'Ella está explicando cómo resolver el ejercicio.' }
                ],
                tip: '🗣️ Pronunciación: "Eks-pléining".'
              }
            ]
          },
          {
            categoryName: '⏱️ Marcadores y Contexto de Tiempo Actual',
            words: [
              {
                term: 'Right now',
                spanish: 'En este mismo momento',
                examples: [
                  { en: 'The teacher is explaining right now.', es: 'El profesor está explicando en este mismo momento.' },
                  { en: 'We are learning new words right now.', es: 'Estamos aprendiendo nuevas palabras justo ahora.' }
                ],
                tip: '💡 Marcador directo de Presente Continuo.'
              },
              {
                term: 'At the moment',
                spanish: 'Por el momento / Actualmente',
                examples: [
                  { en: 'She is working at the moment.', es: 'Ella está trabajando por el momento.' },
                  { en: 'We are studying Unit 1 at the moment.', es: 'Estamos estudiando la Unidad 1 en este momento.' }
                ],
                tip: '🗣️ Expresión equivalente a "right now".'
              },
              {
                term: 'Working together',
                spanish: 'Trabajando juntos / En equipo',
                examples: [
                  { en: 'Students are working together in groups.', es: 'Los estudiantes están trabajando juntos en grupos.' },
                  { en: 'Working together makes learning fun.', es: 'Trabajar juntos hace el aprendizaje divertido.' }
                ],
                tip: '💡 Trabajo colaborativo escolar.'
              },
              {
                term: 'Playing games',
                spanish: 'Jugando juegos educativos',
                examples: [
                  { en: 'They are playing vocabulary games.', es: 'Están jugando juegos de vocabulario.' },
                  { en: 'We are playing an interactive quiz.', es: 'Estamos jugando una prueba interactiva.' }
                ],
                tip: '🗣️ Pronunciación: "Pléi-ing guéims".'
              },
              {
                term: 'Preparing exam',
                spanish: 'Preparando el examen',
                examples: [
                  { en: 'We are preparing for the ICFES test.', es: 'Nos estamos preparando para la prueba ICFES.' },
                  { en: 'She is preparing her oral presentation.', es: 'Ella está preparando su presentación oral.' }
                ],
                tip: '🗣️ Pronunciación: "Pri-péring eg-zám".'
              }
            ]
          }
        ]
      },
      2: {
        unitName: 'Unidad 2: Alimentos, Cantidades y Cuantificadores',
        categories: [
          {
            categoryName: '🍎 Sustantivos y Elementos Incontables',
            words: [
              {
                term: 'Water',
                spanish: 'Agua (Incontable)',
                examples: [
                  { en: 'How much water do you drink daily?', es: '¿Mucha agua bebes a diario?' },
                  { en: 'There is some water in the glass.', es: 'Hay algo de agua en el vaso.' }
                ],
                tip: '💡 Incontable: Usa "How MUCH" en lugar de "How MANY".'
              },
              {
                term: 'Food',
                spanish: 'Comida / Alimentos',
                examples: [
                  { en: 'Healthy food is good for your body.', es: 'La comida saludable es buena para tu cuerpo.' },
                  { en: 'The cafeteria serves warm food.', es: 'La cafetería sirve comida caliente.' }
                ],
                tip: '🗣️ Pronunciación: "Fuud".'
              },
              {
                term: 'Money',
                spanish: 'Dinero (Incontable)',
                examples: [
                  { en: 'How much money do you need?', es: '¿Cuánto dinero necesitas?' },
                  { en: 'She saves money for university.', es: 'Ella ahorra dinero para la universidad.' }
                ],
                tip: '⚠️ El dinero como concepto abstracto en inglés es INCONTABLE (How much money).'
              },
              {
                term: 'Fruit',
                spanish: 'Fruta(s)',
                examples: [
                  { en: 'Eat fresh fruit every day.', es: 'Come fruta fresca todos los días.' },
                  { en: 'Apples and bananas are healthy fruit.', es: 'Las manzanas y bananos son frutas saludables.' }
                ],
                tip: '🗣️ Pronunciación: "Fruut". Generalmente se usa en singular para el grupo.'
              },
              {
                term: 'Bottle',
                spanish: 'Botella (Contable)',
                examples: [
                  { en: 'There are two bottles of water.', es: 'Hay dos botellas de agua.' },
                  { en: 'Bring a water bottle to class.', es: 'Trae una botella de agua a clase.' }
                ],
                tip: '💡 La botella SÍ se cuenta: "two bottles of water".'
              }
            ]
          },
          {
            categoryName: '📊 Cuantificadores (SOME, ANY, MUCH, MANY)',
            words: [
              {
                term: 'Some',
                spanish: 'Alguno(s) / Algo de (Oraciones Afirmativas)',
                examples: [
                  { en: 'I have some questions about the test.', es: 'Tengo algunas preguntas sobre la prueba.' },
                  { en: 'There is some milk in the fridge.', es: 'Hay algo de leche en la nevera.' }
                ],
                tip: '💡 Regla ICFES: SOME se usa en oraciones AFIRMATIVAS.'
              },
              {
                term: 'Any',
                spanish: 'Ningún / Algún (Negativas y Preguntas)',
                examples: [
                  { en: 'Is there any problem with your computer?', es: '¿Hay algún problema con tu computador?' },
                  { en: 'I don\'t have any doubt about the topic.', es: 'No tengo ninguna duda sobre el tema.' }
                ],
                tip: '💡 Regla ICFES: ANY se usa en preguntas y oraciones negativas.'
              },
              {
                term: 'How many',
                spanish: '¿Cuántos? (Para sustantivos contables)',
                examples: [
                  { en: 'How many students are in Grade 701?', es: '¿Cuántos estudiantes hay en Grado 701?' },
                  { en: 'How many apples did you buy?', es: '¿Cuántas manzanas compraste?' }
                ],
                tip: '💡 Usa How MANY para elementos en plural que se pueden contar.'
              },
              {
                term: 'How much',
                spanish: '¿Cuánto? (Para sustantivos incontables)',
                examples: [
                  { en: 'How much time do we have?', es: '¿Cuánto tiempo tenemos?' },
                  { en: 'How much sugar do you want?', es: '¿Cuánta azúcar quieres?' }
                ],
                tip: '💡 Usa How MUCH para líquidos, masa o conceptos incontables.'
              },
              {
                term: 'A lot of',
                spanish: 'Mucho / Gran cantidad de',
                examples: [
                  { en: 'There are a lot of books in the library.', es: 'Hay muchos libros en la biblioteca.' },
                  { en: 'She has a lot of friends at school.', es: 'Ella tiene muchos amigos en el colegio.' }
                ],
                tip: '💡 Funciona tanto para contables como incontables.'
              }
            ]
          }
        ]
      },
      3: {
        unitName: 'Unidad 3: Eventos Pasados (WAS/WERE y Verbos -ED)',
        categories: [
          {
            categoryName: '📜 Expresiones de Tiempo Pasado',
            words: [
              {
                term: 'Yesterday',
                spanish: 'Ayer',
                examples: [
                  { en: 'We were at school yesterday.', es: 'Estuvimos en el colegio ayer.' },
                  { en: 'Sofia studied English yesterday afternoon.', es: 'Sofia estudió inglés ayer por la tarde.' }
                ],
                tip: '🗣️ Pronunciación: "Yésturdei". Marca tiempo pasado completo.'
              },
              {
                term: 'Last week',
                spanish: 'La semana pasada',
                examples: [
                  { en: 'We presented the test last week.', es: 'Presentamos la prueba la semana pasada.' },
                  { en: 'They visited Medellín last week.', es: 'Ellos visitaron Medellín la semana pasada.' }
                ],
                tip: '💡 Last + unidad de tiempo: last month, last year, last Friday.'
              },
              {
                term: 'Ago (hace x tiempo)',
                spanish: 'Hace (ej. hace 2 días)',
                examples: [
                  { en: 'The exam finished two hours ago.', es: 'El examen terminó hace dos horas.' },
                  { en: 'They registered three days ago.', es: 'Se registraron hace tres días.' }
                ],
                tip: '💡 Posición: "Ago" siempre va AL FINAL del período de tiempo (ej. 2 days ago).'
              },
              {
                term: 'Was / Were',
                spanish: 'Estaba / Era (Pasado del verbo To Be)',
                examples: [
                  { en: 'I was very happy with my score.', es: 'Estaba muy feliz con mi puntaje.' },
                  { en: 'They were in grade 6th last year.', es: 'Ellos estaban en grado 6° el año pasado.' }
                ],
                tip: '💡 Regla: WAS con I, He, She, It. WERE con We, You, They.'
              },
              {
                term: 'In 2025',
                spanish: 'En el año 2025',
                examples: [
                  { en: 'The school built the lab in 2025.', es: 'El colegio construyó el laboratorio en 2025.' },
                  { en: 'She was born in 2012.', es: 'Ella nació en 2012.' }
                ],
                tip: '💡 Los años pasados exigen el uso de verbos en pasado.'
              }
            ]
          },
          {
            categoryName: '⚽ Verbos Regulares en Pasado (-ED)',
            words: [
              {
                term: 'Played',
                spanish: 'Jugó / Jugamos (Pasado de play)',
                examples: [
                  { en: 'We played football yesterday.', es: 'Jugamos fútbol ayer.' },
                  { en: 'He played the guitar at the concert.', es: 'Él tocó la guitarra en el concierto.' }
                ],
                tip: '🗣️ Pronunciación: "Pleid". La ed suena como una d suave.'
              },
              {
                term: 'Studied',
                spanish: 'Estudió / Estudiamos (Pasado de study)',
                examples: [
                  { en: 'She studied for the ICFES test.', es: 'Ella estudió para la prueba ICFES.' },
                  { en: 'We studied Unit 3 together.', es: 'Estudiamos la Unidad 3 juntos.' }
                ],
                tip: '⚠️ Ortografía: Study cambia y por ied: studied.'
              },
              {
                term: 'Visited',
                spanish: 'Visitó / Visitamos (Pasado de visit)',
                examples: [
                  { en: 'They visited the English Lab.', es: 'Ellos visitaron el laboratorio de inglés.' },
                  { en: 'I visited my grandmother last Sunday.', es: 'Visité a mi abuela el domingo pasado.' }
                ],
                tip: '🗣️ Pronunciación: "Vísited". Aquí la ed suena como id.'
              },
              {
                term: 'Walked',
                spanish: 'Caminó / Caminamos (Pasado de walk)',
                examples: [
                  { en: 'We walked to school in the morning.', es: 'Caminamos al colegio en la mañana.' },
                  { en: 'He walked 2 kilometers yesterday.', es: 'Él caminó 2 kilómetros ayer.' }
                ],
                tip: '🗣️ Pronunciación: "Wokt". La l no suena y la ed suena como t.'
              },
              {
                term: 'Worked',
                spanish: 'Trabajó / Trabajamos (Pasado de work)',
                examples: [
                  { en: 'My parents worked hard all week.', es: 'Mis padres trabajaron duro toda la semana.' },
                  { en: 'Students worked in pairs.', es: 'Los estudiantes trabajaron en parejas.' }
                ],
                tip: '🗣️ Pronunciación: "Workt".'
              }
            ]
          }
        ]
      },
      4: {
        unitName: 'Unidad 4: Verbos Irregulares y Avisos Públicos',
        categories: [
          {
            categoryName: '🔀 Verbos Irregulares en Pasado',
            words: [
              {
                term: 'Went (Pasado de GO)',
                spanish: 'Fui / Fue / Fuimos (Pasado de ir)',
                examples: [
                  { en: 'We went to the English lab yesterday.', es: 'Fuimos al laboratorio de inglés ayer.' },
                  { en: 'Did you go? -> Yes, I went.', es: '¿Fuiste? -> Sí, fui.' }
                ],
                tip: '⚠️ Irregular: "Go" cambia totalmente a "went" en pasado.'
              },
              {
                term: 'Saw (Pasado de SEE)',
                spanish: 'Vio / Vi / Vimos (Pasado de ver)',
                examples: [
                  { en: 'I saw the notice on the board.', es: 'Vi el aviso en la cartelera.' },
                  { en: 'She saw her scores online.', es: 'Ella vio sus notas en línea.' }
                ],
                tip: '🗣️ Pronunciación: "Soo".'
              },
              {
                term: 'Wrote (Pasado de WRITE)',
                spanish: 'Escribió / Escribí (Pasado de escribir)',
                examples: [
                  { en: 'He wrote an essay in English.', es: 'Él escribió un ensayo en inglés.' },
                  { en: 'They wrote their names on the list.', es: 'Ellos escribieron sus nombres en la lista.' }
                ],
                tip: '🗣️ Pronunciación: "Rout".'
              },
              {
                term: 'Bought (Pasado de BUY)',
                spanish: 'Compró / Compré (Pasado de comprar)',
                examples: [
                  { en: 'She bought a new dictionary.', es: 'Ella compró un nuevo diccionario.' },
                  { en: 'I bought school supplies yesterday.', es: 'Compré útiles escolares ayer.' }
                ],
                tip: '🗣️ Pronunciación: "Boot".'
              },
              {
                term: 'Ate (Pasado de EAT)',
                spanish: 'Comió / Comí (Pasado de comer)',
                examples: [
                  { en: 'We ate lunch at the cafeteria.', es: 'Almorzamos en la cafetería.' },
                  { en: 'He ate a healthy apple.', es: 'Él se comió una manzana saludable.' }
                ],
                tip: '🗣️ Pronunciación: "Eit".'
              }
            ]
          },
          {
            categoryName: '📍 Términos de Avisos Públicos (ICFES Parte 1)',
            words: [
              {
                term: 'Notice board',
                spanish: 'Cartelera / Tablero de avisos',
                examples: [
                  { en: 'Read the announcement on the notice board.', es: 'Lee el anuncio en la cartelera.' },
                  { en: 'Where can you see this sign? On a notice board.', es: '¿Dónde ves este aviso? En una cartelera.' }
                ],
                tip: '💡 Clave ICFES Parte 1: Ubicación habitual de avisos escolares.'
              },
              {
                term: 'Emergency exit',
                spanish: 'Salida de emergencia',
                examples: [
                  { en: 'Keep the emergency exit clear at all times.', es: 'Mantén libre la salida de emergencia en todo momento.' },
                  { en: 'Follow the green signs to the emergency exit.', es: 'Sigue los avisos verdes hacia la salida de emergencia.' }
                ],
                tip: '📍 Aviso común en edificios públicos.'
              },
              {
                term: 'Keep clear',
                spanish: 'Mantener despejado / No obstruir',
                examples: [
                  { en: 'Keep clear of the exit door.', es: 'Mantén despejada la puerta de salida.' },
                  { en: 'Keep clear - construction area.', es: 'No obstruir - área de construcción.' }
                ],
                tip: '💡 Indica prohibición de dejar objetos en el camino.'
              },
              {
                term: 'Did / Didn\'t',
                spanish: 'Auxiliar de pasado afirmativo / negativo',
                examples: [
                  { en: 'Did you complete the assignment? -> Yes, I did.', es: '¿Completaste la tarea? -> Sí, la completé.' },
                  { en: 'I didn\'t understand the question.', es: 'No entendí la pregunta.' }
                ],
                tip: '⚠️ Recuerda: Después de Didn\'t el verbo vuelve a su forma base (didn\'t understand).'
              },
              {
                term: 'Had (Pasado de HAVE)',
                spanish: 'Tuvo / Teníamos (Pasado de tener)',
                examples: [
                  { en: 'We had an English test yesterday.', es: 'Tuvimos una prueba de inglés ayer.' },
                  { en: 'She had a question about the assignment.', es: 'Ella tenía una duda sobre la tarea.' }
                ],
                tip: '🗣️ Pronunciación: "Jad".'
              }
            ]
          }
        ]
      },
      5: {
        unitName: 'Unidad 5: Comparativos y Entornos',
        categories: [
          {
            categoryName: '📊 Expresiones de Comparación',
            words: [
              {
                term: 'Bigger than',
                spanish: 'Más grande que',
                examples: [
                  { en: 'Medellín is bigger than Envigado.', es: 'Medellín es más grande que Envigado.' },
                  { en: 'Our school is bigger than last year.', es: 'Nuestro colegio es más grande que el año pasado.' }
                ],
                tip: '💡 Regla: Adjetivos cortos duplican la consonante final: big -> bigger.'
              },
              {
                term: 'Faster than',
                spanish: 'Más rápido que',
                examples: [
                  { en: 'Cars are faster than bicycles.', es: 'Los carros son más rápidos que las bicicletas.' },
                  { en: 'She reads faster than before.', es: 'Ella lee más rápido que antes.' }
                ],
                tip: '🗣️ Fast + er than = faster than.'
              },
              {
                term: 'Taller than',
                spanish: 'Más alto que (estatura)',
                examples: [
                  { en: 'Mateo is taller than David.', es: 'Mateo es más alto que David.' },
                  { en: 'The new building is taller than the old one.', es: 'El edificio nuevo es más alto que el antiguo.' }
                ],
                tip: '💡 Se usa para medir altura de personas o estructuras.'
              },
              {
                term: 'Easier than',
                spanish: 'Más fácil que',
                examples: [
                  { en: 'This exercise is easier than the first one.', es: 'Este ejercicio es más fácil que el primero.' },
                  { en: 'Practice makes English easier than before.', es: 'La práctica hace el inglés más fácil que antes.' }
                ],
                tip: '⚠️ Ortografía: Easy cambia la y por ier: easier.'
              },
              {
                term: 'Better than',
                spanish: 'Mejor que (Comparativo irregular de GOOD)',
                examples: [
                  { en: 'My current score is better than last week.', es: 'Mi puntaje actual es mejor que el de la semana pasada.' },
                  { en: 'Working together is better than working alone.', es: 'Trabajar juntos es mejor que trabajar solo.' }
                ],
                tip: '⚠️ Irregular: El comparativo de Good es BETTER THAN (no existe "gooder").'
              }
            ]
          },
          {
            categoryName: '🏆 Expresiones de Grado Superlativo',
            words: [
              {
                term: 'More interesting',
                spanish: 'Más interesante',
                examples: [
                  { en: 'Reading stories is more interesting.', es: 'Leer historias es más interesante.' },
                  { en: 'This unit is more interesting than the previous one.', es: 'Esta unidad es más interesante que la anterior.' }
                ],
                tip: '💡 Regla: Adjetivos de 3 o más sílabas usan MORE ... THAN.'
              },
              {
                term: 'The best',
                spanish: 'El mejor / La mejor (Superlativo de GOOD)',
                examples: [
                  { en: 'Mr. Didier is the best English teacher.', es: 'El Prof. Didier es el mejor profesor de inglés.' },
                  { en: 'This is the best score of the class.', es: 'Este es el mejor puntaje de la clase.' }
                ],
                tip: '⚠️ Irregular: El superlativo de Good es THE BEST.'
              },
              {
                term: 'More expensive',
                spanish: 'Más costoso / Más caro',
                examples: [
                  { en: 'Computers are more expensive than notebooks.', es: 'Los computadores son más costosos que los cuadernos.' },
                  { en: 'This book is more expensive than that one.', es: 'Este libro es más caro que aquel.' }
                ],
                tip: '🗣️ Pronunciación: "Mor eks-pénsiv".'
              },
              {
                term: 'The most important',
                spanish: 'Lo más importante / El más relevante',
                examples: [
                  { en: 'Education is the most important tool for the future.', es: 'La educación es la herramienta más importante para el futuro.' },
                  { en: 'Vocabulary is the most important part of ICFES.', es: 'El vocabulario es la parte más importante del ICFES.' }
                ],
                tip: '💡 Superlativo largo: THE MOST + adjetivo.'
              },
              {
                term: 'Smaller than',
                spanish: 'Más pequeño que',
                examples: [
                  { en: 'A pencil is smaller than a dictionary.', es: 'Un lápiz es más pequeño que un diccionario.' },
                  { en: 'Classroom 601 is smaller than the auditorium.', es: 'El salón 601 es más pequeño que el auditorio.' }
                ],
                tip: '🗣️ Small + er than = smaller than.'
            }
          ]
        }
      ]
    }
  },
  '9': {
        1: {
          unitName: 'Unidad 1: Experiencias de Vida (Presente Perfecto)',
          categories: [
            {
              categoryName: '🌟 Conectores y Palabras Clave de Experiencia',
              words: [
                {
                  term: 'Ever',
                  spanish: '¿Alguna vez? (en preguntas de experiencia)',
                  examples: [
                    { en: 'Have you ever visited San Andrés?', es: '¿Alguna vez has visitado San Andrés?' },
                    { en: 'Has she ever presented an ICFES test?', es: '¿Alguna vez ha presentado ella una prueba ICFES?' }
                  ],
                  tip: '💡 Tip ICFES: EVER se usa exclusivamente en PREGUNTAS.'
                },
                {
                  term: 'Never',
                  spanish: 'Nunca (con sentido negativo afirmativo)',
                  examples: [
                    { en: 'I have never failed an English test.', es: 'Nunca he reprobado un examen de inglés.' },
                    { en: 'They have never traveled by airplane.', es: 'Ellos nunca han viajado en avión.' }
                  ],
                  tip: '⚠️ Regla: NEVER ya es negativo, NO requiere "not" (I have never).'
                },
                {
                  term: 'Already',
                  spanish: 'Ya (acción ya completada)',
                  examples: [
                    { en: 'I have already finished Unit 1.', es: 'Ya he terminado la Unidad 1.' },
                    { en: 'She has already submitted her exam.', es: 'Ella ya ha enviado su examen.' }
                  ],
                  tip: '💡 Se coloca entre have/has y el verbo principal.'
                },
                {
                  term: 'Yet',
                  spanish: 'Aún / Todavía (al final de negativas y preguntas)',
                  examples: [
                    { en: 'I haven\'t finished the test yet.', es: 'Aún no he terminado el examen.' },
                    { en: 'Have you registered your name yet?', es: '¿Ya registraste tu nombre?' }
                  ],
                  tip: '💡 Ubicación: YET siempre va AL FINAL de la oración.'
                },
                {
                  term: 'So far',
                  spanish: 'Hasta el momento / Hasta ahora',
                  examples: [
                    { en: 'So far, I have earned 500 XP.', es: 'Hasta ahora, he ganado 500 XP.' },
                    { en: 'We have completed 3 units so far.', es: 'Hemos completado 3 unidades hasta el momento.' }
                  ],
                  tip: '🗣️ Expresión de progreso continuo.'
                }
              ]
            },
            {
              categoryName: '🏆 Verbos en Participio Pasado (V3)',
              words: [
                {
                  term: 'Achieved',
                  spanish: 'Alcanzado / Logrado',
                  examples: [
                    { en: 'Sofia has achieved an A2 English level.', es: 'Sofia ha alcanzado un nivel de inglés A2.' },
                    { en: 'They have achieved their academic goals.', es: 'Ellos han logrado sus metas académicas.' }
                  ],
                  tip: '🗣️ Pronunciación: "Achívd". Participio de achieve.'
                },
                {
                  term: 'Have visited',
                  spanish: 'He visitado / Hemos visitado',
                  examples: [
                    { en: 'I have visited Medellín many times.', es: 'He visitado Medellín muchas veces.' },
                    { en: 'We have visited the university campus.', es: 'Hemos visitado el campus universitario.' }
                  ],
                  tip: '💡 Estructura de Presente Perfecto.'
                },
                {
                  term: 'Has finished',
                  spanish: 'Ha terminado (él/ella)',
                  examples: [
                    { en: 'She has finished all 15 questions.', es: 'Ella ha terminado las 15 preguntas.' },
                    { en: 'The student has finished his assignment.', es: 'El estudiante ha terminado su tarea.' }
                  ],
                  tip: '💡 Has se usa con He, She, It.'
                },
                {
                  term: 'Experience',
                  spanish: 'Experiencia de vida o académica',
                  examples: [
                    { en: 'Learning languages is a great experience.', es: 'Aprender idiomas es una gran experiencia.' },
                    { en: 'Do you have work experience?', es: '¿Tienes experiencia laboral?' }
                  ],
                  tip: '🗣️ Pronunciación: "Eks-píriens".'
                },
                {
                  term: 'Improvement',
                  spanish: 'Mejora / Progreso continuo',
                  examples: [
                    { en: 'Your score shows a great improvement.', es: 'Tu puntaje muestra una gran mejora.' },
                    { en: 'Practice leads to constant improvement.', es: 'La práctica lleva a una mejora constante.' }
                  ],
                  tip: '💡 Sustantivo derivado del verbo improve (mejorar).'
                }
              ]
            }
          ]
        },
        2: {
          unitName: 'Unidad 2: Historias e Interrupciones (WHEN / WHILE)',
          categories: [
            {
              categoryName: '⏳ Conectores y Tiempos de Pasado',
              words: [
                {
                  term: 'While (Mientras)',
                  spanish: 'Mientras (introduce la acción larga en progreso)',
                  examples: [
                    { en: 'While I was studying, my phone rang.', es: 'Mientras estaba estudiando, sonó mi teléfono.' },
                    { en: 'They were listening while the teacher spoke.', es: 'Estaban escuchando mientras el profesor hablaba.' }
                  ],
                  tip: '💡 Clave ICFES: WHILE exige el uso de Pasado Continuo (was/were -ing).'
                },
                {
                  term: 'When (Cuando)',
                  spanish: 'Cuando (introduce la interrupción corta)',
                  examples: [
                    { en: 'I was reading WHEN the exam started.', es: 'Estaba leyendo CUANDO empezó el examen.' },
                    { en: 'When did you finish your homework?', es: '¿Cuándo terminaste tu tarea?' }
                  ],
                  tip: '💡 Clave ICFES: WHEN introduce el Pasado Simple.'
                },
                {
                  term: 'Was reading',
                  spanish: 'Estaba leyendo',
                  examples: [
                    { en: 'I was reading an ICFES text yesterday.', es: 'Estaba leyendo un texto del ICFES ayer.' },
                    { en: 'She was reading when the bell rang.', es: 'Ella estaba leyendo cuando sonó la campana.' }
                  ],
                  tip: '💡 Estructura de Pasado Continuo.'
                },
                {
                  term: 'Were playing',
                  spanish: 'Estaban jugando / Estábamos jugando',
                  examples: [
                    { en: 'They were playing basketball in the yard.', es: 'Estaban jugando baloncesto en el patio.' },
                    { en: 'We were playing when it started to rain.', es: 'Estábamos jugando cuando empezó a llover.' }
                  ],
                  tip: '💡 Were se usa con plurales (We, They).'
                },
                {
                  term: 'Rang (Pasado de RING)',
                  spanish: 'Sonó (el teléfono o la campana)',
                  examples: [
                    { en: 'The alarm rang at 6:00 AM.', es: 'La alarma sonó a las 6:00 AM.' },
                    { en: 'The school bell rang for break.', es: 'La campana del colegio sonó para el descanso.' }
                  ],
                  tip: '🗣️ Pronunciación: "Rang". Ring -> Rang.'
                }
              ]
            },
            {
              categoryName: '🎬 Verbos de Evento en Contexto Narrativo',
              words: [
                {
                  term: 'Interrupted',
                  spanish: 'Interrumpió / Interrumpido',
                  examples: [
                    { en: 'A loud noise interrupted the exam.', es: 'Un ruido fuerte interrumpió el examen.' },
                    { en: 'Don\'t interrupt while someone is speaking.', es: 'No interrumpas mientras alguien está hablando.' }
                  ],
                  tip: '🗣️ Pronunciación: "Inter-ráptid".'
                },
                {
                  term: 'Suddenly',
                  spanish: 'De repente / De imprevisto',
                  examples: [
                    { en: 'Suddenly, the lights went off.', es: 'De repente, las luces se apagaron.' },
                    { en: 'Suddenly, the teacher entered the room.', es: 'De imprevisto, el profesor entró al salón.' }
                  ],
                  tip: '💡 Adverbio frecuente en pasajes narrativos del ICFES.'
                },
                {
                  term: 'Arrived',
                  spanish: 'Llegó / Llegamos',
                  examples: [
                    { en: 'We arrived at school on time.', es: 'Llegamos al colegio a tiempo.' },
                    { en: 'She was studying when the bus arrived.', es: 'Ella estaba estudiando cuando llegó el bus.' }
                  ],
                  tip: '🗣️ Pronunciación: "Erráivd".'
                },
                {
                  term: 'Happened',
                  spanish: 'Sucedió / Ocurrió',
                  examples: [
                    { en: 'What happened yesterday afternoon?', es: '¿Qué sucedió ayer por la tarde?' },
                    { en: 'The accident happened on the main street.', es: 'El accidente ocurrió en la calle principal.' }
                  ],
                  tip: '🗣️ Pronunciación: "Jápend".'
                },
                {
                  term: 'During',
                  spanish: 'Durante (un período de tiempo)',
                  examples: [
                    { en: 'No mobile phones during the exam.', es: 'Sin celulares durante el examen.' },
                    { en: 'It rained heavily during the night.', es: 'Llovió fuertemente durante la noche.' }
                  ],
                  tip: '📍 Frecuente en avisos de prohibición escolar.'
                }
              ]
            }
          ]
        },
        3: {
          unitName: 'Unidad 3: Planes Futuros, Profesiones y Metas',
          categories: [
            {
              categoryName: '🚀 Futuro, Metas y Proyectos de Vida',
              words: [
                {
                  term: 'Scholarship',
                  spanish: 'Beca universitaria de estudio',
                  examples: [
                    { en: 'Laura wants to win a university scholarship.', es: 'Laura quiere ganar una beca universitaria.' },
                    { en: 'Higher education scholarships require high scores.', es: 'Las becas universitarias requieren puntajes altos.' }
                  ],
                  tip: '🗣️ Pronunciación: "Skólar-ship". Muy frecuente en Lectura Crítica.'
                },
                {
                  term: 'Be going to',
                  spanish: 'Ir a (plan estructurado u organizado)',
                  examples: [
                    { en: 'I am going to study software engineering.', es: 'Voy a estudiar ingeniería de software.' },
                    { en: 'She is going to present her exam tomorrow.', es: 'Ella va a presentar su examen mañana.' }
                  ],
                  tip: '💡 Indica intención o plan previo decidido antes de hablar.'
                },
                {
                  term: 'Will',
                  spanish: 'Auxiliar de futuro (decisiones/predicciones)',
                  examples: [
                    { en: 'I will help you with your homework.', es: 'Te ayudaré con tu tarea.' },
                    { en: 'AI tools will transform future careers.', es: 'Las herramientas de IA transformarán las carreras futuras.' }
                  ],
                  tip: '💡 Se usa para predicciones u ofrecimientos espontáneos.'
                },
                {
                  term: 'Goal / Target',
                  spanish: 'Meta / Propósito a alcanzar',
                  examples: [
                    { en: 'My main goal is to achieve an A2 level.', es: 'Mi meta principal es alcanzar un nivel A2.' },
                    { en: 'Set clear targets for your study routine.', es: 'Establece metas claras para tu rutina de estudio.' }
                  ],
                  tip: '🗣️ Pronunciación: "Goul" y "Tárguet".'
                },
                {
                  term: 'Career',
                  spanish: 'Carrera profesional u oficio',
                  examples: [
                    { en: 'She wants to pursue a career in medicine.', es: 'Ella quiere seguir una carrera en medicina.' },
                    { en: 'Choosing a future career requires dedication.', es: 'Elegir una carrera futura requiere dedicación.' }
                  ],
                  tip: '⚠️ Falso amigo: "Career" es carrera profesional (NO carrera de velocidad: race).'
                }
              ]
            },
            {
              categoryName: '🎓 Profesiones y Ámbitos Académicos',
              words: [
                {
                  term: 'University',
                  spanish: 'Universidad / Educación Superior',
                  examples: [
                    { en: 'University admissions open next month.', es: 'Las admisiones universitarias abren el próximo mes.' },
                    { en: 'He plans to study at the public university.', es: 'Él planea estudiar en la universidad pública.' }
                  ],
                  tip: '🗣️ Pronunciación: "Yú-ni-vér-siti".'
                },
                {
                  term: 'Engineering',
                  spanish: 'Ingeniería',
                  examples: [
                    { en: 'Systems engineering is a popular field.', es: 'La ingeniería de sistemas es un campo popular.' },
                    { en: 'She is going to study civil engineering.', es: 'Ella va a estudiar ingeniería civil.' }
                  ],
                  tip: '🗣️ Pronunciación: "En-ji-ní-ring".'
                },
                {
                  term: 'Software',
                  spanish: 'Programación / Software informático',
                  examples: [
                    { en: 'Medellín is a center for software development.', es: 'Medellín es un centro de desarrollo de software.' },
                    { en: 'Learn software tools for your career.', es: 'Aprende herramientas de software para tu carrera.' }
                  ],
                  tip: '💡 Término tecnológico global.'
                },
                {
                  term: 'In the future',
                  spanish: 'En el futuro',
                  examples: [
                    { en: 'What do you want to be in the future?', es: '¿Qué quieres ser en el futuro?' },
                    { en: 'In the future, languages will be essential.', es: 'En el futuro, los idiomas serán esenciales.' }
                  ],
                  tip: '🗣️ Pronunciación: "In da fiú-chur".'
                },
                {
                  term: 'Achieve dreams',
                  spanish: 'Alcanzar o cumplir los sueños',
                  examples: [
                    { en: 'Hard work helps you achieve your dreams.', es: 'El trabajo duro te ayuda a alcanzar tus sueños.' },
                    { en: 'She achieved her dream of entering university.', es: 'Ella cumplió su sueño de entrar a la universidad.' }
                  ],
                  tip: '💡 Expresión de motivación personal.'
                }
              ]
            }
          ]
        },
        4: {
          unitName: 'Unidad 4: Condicionales y Relación Causa-Efecto',
          categories: [
            {
              categoryName: '🎯 Estructuras de Condición y Resultado',
              words: [
                {
                  term: 'If (Si condicional)',
                  spanish: 'Si (condición o supuesto)',
                  examples: [
                    { en: 'IF you practice daily, you WILL improve.', es: 'SI practicas a diario, MEJORARÁS.' },
                    { en: 'If we study together, we will pass.', es: 'Si estudiamos juntos, aprobaremos.' }
                  ],
                  tip: '⚠️ Alerta ICFES: La cláusula con "IF" va en Presente Simple (NO lleva "will").'
                },
                {
                  term: 'Opportunity',
                  spanish: 'Oportunidad (académica o laboral)',
                  examples: [
                    { en: 'English opens global job opportunities.', es: 'El inglés abre oportunidades laborales globales.' },
                    { en: 'Take this opportunity to study hard.', es: 'Aprovecha esta oportunidad para estudiar duro.' }
                  ],
                  tip: '🗣️ Pronunciación: "Opor-tiú-niti". Plural: opportunities.'
                },
                {
                  term: 'Pass the test',
                  spanish: 'Aprobar el examen',
                  examples: [
                    { en: 'If you study, you will pass the test.', es: 'Si estudias, aprobarás el examen.' },
                    { en: 'She passed the ICFES evaluation with 90%.', es: 'Ella aprobó la evaluación ICFES con 90%.' }
                  ],
                  tip: '💡 Pass = aprobar / Fail = reprobar.'
                },
                {
                  term: 'Unless',
                  spanish: 'A menos que / Salvo que',
                  examples: [
                    { en: 'You won\'t pass UNLESS you study.', es: 'No aprobarás A MENOS QUE estudies.' },
                    { en: 'Unless it rains, we will play outside.', es: 'A menos que llueva, jugaremos afuera.' }
                  ],
                  tip: '💡 Equivalente negativo de IF (Unless = If not).'
                },
                {
                  term: 'Result',
                  spanish: 'Resultado final',
                  examples: [
                    { en: 'Check your evaluation results online.', es: 'Revisa tus resultados de evaluación en línea.' },
                    { en: 'Good results require daily effort.', es: 'Los buenos resultados requieren esfuerzo diario.' }
                  ],
                  tip: '🗣️ Pronunciación: "Ri-zált".'
                }
              ]
            },
            {
              categoryName: '📈 Términos de Desempeño y Éxito',
              words: [
                {
                  term: 'Success',
                  spanish: 'Éxito / Logro destacado',
                  examples: [
                    { en: 'Education is the key to personal success.', es: 'La educación es la clave del éxito personal.' },
                    { en: 'We wish you great success in your test.', es: 'Te deseamos gran éxito en tu prueba.' }
                  ],
                  tip: '🗣️ Pronunciación: "Suk-sés". Adjetivo: Successful.'
                },
                {
                  term: 'Consequence',
                  spanish: 'Consecuencia o resultado directo',
                  examples: [
                    { en: 'High scores are the consequence of study.', es: 'Puntajes altos son la consecuencia del estudio.' },
                    { en: 'Understand the consequences of your choices.', es: 'Entiende las consecuencias de tus elecciones.' }
                  ],
                  tip: '🗣️ Pronunciación: "Kón-si-kuens".'
                },
                {
                  term: 'High score',
                  spanish: 'Puntaje alto de desempeño',
                  examples: [
                    { en: 'She achieved a high score on the ICFES test.', es: 'Ella logró un puntaje alto en la prueba ICFES.' },
                    { en: 'High scores open scholarship doors.', es: 'Puntajes altos abren puertas de becas.' }
                  ],
                  tip: '💡 Meta de evaluación académica.'
                },
                {
                  term: 'Daily practice',
                  spanish: 'Práctica diaria constante',
                  examples: [
                    { en: 'Daily practice improves reading speed.', es: 'La práctica diaria mejora la velocidad de lectura.' },
                    { en: 'Commit to 15 minutes of daily practice.', es: 'Comprométete a 15 minutos de práctica diaria.' }
                  ],
                  tip: '💡 Consejo pedagógico recurrente del Prof. Didier.'
                },
                {
                  term: 'If you study',
                  spanish: 'Si estudias (condicional estándar)',
                  examples: [
                    { en: 'If you study, you will succeed.', es: 'Si estudias, tendrás éxito.' },
                    { en: 'If you study Unit 4, it will be easy.', es: 'Si estudias la Unidad 4, será fácil.' }
                  ],
                  tip: '💡 Frase clásica en ejercicios de Primer Condicional.'
                }
              ]
            }
          ]
        },
        5: {
          unitName: 'Unidad 5: Verbos Modales y Lectura Crítica ICFES',
          categories: [
            {
              categoryName: '⚖️ Verbos Modales de Norma, Consejo y Necesidad',
              words: [
                {
                  term: 'Must / Must not',
                  spanish: 'Deber (Obligación estricta / Prohibición)',
                  examples: [
                    { en: 'Students MUST wear the official uniform.', es: 'Los estudiantes DEBEN usar el uniforme oficial.' },
                    { en: 'You MUST NOT use cellphones in the exam.', es: 'NO DEBES usar celulares en el examen.' }
                  ],
                  tip: '💡 Clave ICFES: MUST indica norma obligatoria o prohibición estricta.'
                },
                {
                  term: 'Should / Should not',
                  spanish: 'Deberías (Recomendación o consejo amigable)',
                  examples: [
                    { en: 'You SHOULD read carefully before answering.', es: 'DEBERÍAS leer con cuidado antes de responder.' },
                    { en: 'You SHOULD NOT sleep late before the exam.', es: 'NO DEBERÍAS dormir tarde antes de la prueba.' }
                  ],
                  tip: '💡 Clave ICFES: SHOULD se usa para dar o solicitar consejos.'
                },
                {
                  term: 'Have to / Has to',
                  spanish: 'Tener que (Obligación por regla externa)',
                  examples: [
                    { en: 'We HAVE TO answer all 15 questions.', es: 'TENEMOS QUE responder las 15 preguntas.' },
                    { en: 'He HAS TO present his ID number.', es: 'Él TIENE QUE presentar su número de ID.' }
                  ],
                  tip: '💡 Have to se usa con I, We, They / Has to con He, She.'
                },
                {
                  term: 'May / Might',
                  spanish: 'Poder / Ser posible (Posibilidad o permiso educado)',
                  examples: [
                    { en: 'May I ask a question, teacher?', es: '¿Puedo hacer una pregunta, profesor?' },
                    { en: 'It MIGHT rain this afternoon.', es: 'PODRÍA llover esta tarde (posibilidad).' }
                  ],
                  tip: '💡 MAY expresa permiso educado o posibilidad; MIGHT indica posibilidad remota.'
                },
                {
                  term: 'Required',
                  spanish: 'Requerido / Obligatorio por norma',
                  examples: [
                    { en: 'A 6-digit PIN is required to start.', es: 'Un PIN de 6 dígitos es requerido para iniciar.' },
                    { en: 'ID numbers are required for registration.', es: 'Se requieren números de ID para el registro.' }
                  ],
                  tip: '🗣️ Pronunciación: "Ri-kuáierd".'
                }
              ]
            },
            {
              categoryName: '📑 Términos de Lectura Crítica (ICFES Partes 5 y 6)',
              words: [
                {
                  term: 'Main idea',
                  spanish: 'Idea principal del texto',
                  examples: [
                    { en: 'What is the main idea of paragraph 1?', es: '¿Cuál es la idea principal del párrafo 1?' },
                    { en: 'Identify the main idea before choosing.', es: 'Identifica la idea principal antes de elegir.' }
                  ],
                  tip: '💡 Pregunta típica ICFES: "What is the main idea of the text?".'
                },
                {
                  term: 'Author\'s purpose',
                  spanish: 'Propósito u objetivo del autor',
                  examples: [
                    { en: 'What is the author\'s purpose in this text?', es: '¿Cuál es el propósito del autor en este texto?' },
                    { en: 'The author\'s purpose is to inform students.', es: 'El propósito del autor es informar a los estudiantes.' }
                  ],
                  tip: '💡 Opciones habituales: To inform (informar), To persuade (persuadir), To warn (advertir).'
                },
                {
                  term: 'Critical reading',
                  spanish: 'Lectura crítica e interpretativa',
                  examples: [
                    { en: 'Critical reading skills help you excel.', es: 'Las habilidades de lectura crítica te ayudan a destacar.' },
                    { en: 'Practice critical reading with ICFES texts.', es: 'Practica la lectura crítica con textos del ICFES.' }
                  ],
                  tip: '🗣️ Competencia clave evaluada en Grado 9°.'
                },
                {
                  term: 'According to',
                  spanish: 'Según / De acuerdo con el texto',
                  examples: [
                    { en: 'According to the text, how old is Sofia?', es: 'Según el texto, ¿cuántos años tiene Sofia?' },
                    { en: 'According to Mandela, education changes the world.', es: 'Según Mandela, la educación cambia el mundo.' }
                  ],
                  tip: '💡 Expresión estándar en preguntas de comprensión directa.'
                },
                {
                  term: 'Thesis / Conclusion',
                  spanish: 'Tesis principal / Conclusión final',
                  examples: [
                    { en: 'Select the statement that best concludes the text.', es: 'Selecciona la declaración que mejor concluye el texto.' },
                    { en: 'The main thesis is stated in the introduction.', es: 'La tesis principal se afirma en la introducción.' }
                  ],
                  tip: '🗣️ Pronunciación: "Thí-sis" y "Kon-klú-shon".'
                }
              ]
            }
          ]
        }
      }
    };

export default function UnitVocabularyHub() {
  const [selectedGrade, setSelectedGrade] = useState('6');
  const [selectedUnit, setSelectedUnit] = useState(1);
  const [activeWordData, setActiveWordData] = useState(null);

  const handleWordClick = async (cleanWord) => {
    const data = await getSpanishMeaning(cleanWord);
    setActiveWordData(data);
  };

  const currentUnitData = UNIT_VOCABULARY_DATABASE[selectedGrade]?.[selectedUnit] || UNIT_VOCABULARY_DATABASE['6'][1];

  // Contar el total de palabras en la unidad seleccionada
  const totalWordsInUnit = currentUnitData.categories.reduce((acc, cat) => acc + cat.words.length, 0);

  return (
    <div className="space-y-6 text-slate-100 animate-fadeIn">
      
      {/* ENCABEZADO DEL MÓDULO */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 border border-emerald-500/40 p-6 md:p-8 rounded-3xl shadow-2xl space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-300 bg-emerald-900 px-3 py-1 rounded-full border border-emerald-500/40">
              Módulo de Vocabulario & Contexto Comunicativo (10+ Palabras por Unidad)
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white mt-2 flex items-center gap-2">
              <span>📚</span> Vocabulario por Unidad (Grados 6°, 7° y 9°)
            </h2>
            <p className="text-xs md:text-sm text-slate-300 mt-1 max-w-2xl">
              Explora al menos 10 palabras y expresiones de alta frecuencia por unidad organizadas por categorías. Cada ficha incluye significado en español, al menos dos oraciones bilingües de ejemplo y tips de uso / pronunciación para el ICFES.
            </p>
          </div>
        </div>

        {/* SELECTORES DE GRADO Y UNIDAD */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-800">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              1. Seleccionar Grado:
            </label>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full bg-slate-950 border border-emerald-500/50 rounded-xl px-4 py-3 text-xs font-bold text-white outline-none focus:border-emerald-400 transition cursor-pointer"
            >
              <option value="6">Grado 6° — Fundamentos A1 (10 palabras por unidad)</option>
              <option value="7">Grado 7° — Task-Based Learning A1-A2 (10 palabras por unidad)</option>
              <option value="9">Grado 9° — Competencias Avanzadas ICFES A2-B1 (10 palabras por unidad)</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
              2. Seleccionar Unidad a Repasar:
            </label>
            <div className="grid grid-cols-5 gap-1.5">
              {[1, 2, 3, 4, 5].map((uNum) => (
                <button
                  key={uNum}
                  onClick={() => setSelectedUnit(uNum)}
                  className={`py-2.5 rounded-xl text-xs font-extrabold transition cursor-pointer border ${
                    selectedUnit === uNum
                      ? 'bg-emerald-600 border-emerald-400 text-white shadow-lg scale-105'
                      : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  U{uNum}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CONTENIDO DE LA UNIDAD SELECCIONADA */}
      <div className="bg-slate-900/90 border border-slate-800 p-6 md:p-8 rounded-3xl space-y-8 shadow-2xl">
        
        <div className="border-b border-slate-800 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-400">
              Grado {selectedGrade}° — Unidad {selectedUnit}
            </span>
            <h3 className="text-xl md:text-2xl font-black text-white mt-1">
              {currentUnitData.unitName}
            </h3>
          </div>

          <span className="bg-emerald-950 border border-emerald-500/40 text-emerald-300 px-3.5 py-1 rounded-full text-xs font-extrabold self-start sm:self-auto">
            ✨ {totalWordsInUnit} Palabras de Alta Frecuencia Cargadas
          </span>
        </div>

        {/* LISTADO DE CATEGORÍAS TEMÁTICAS */}
        <div className="space-y-8">
          {currentUnitData.categories.map((cat, catIdx) => (
            <div key={catIdx} className="space-y-4">
              
              <h4 className="text-base font-black text-emerald-300 uppercase tracking-wider flex items-center gap-2 border-b border-slate-800 pb-2">
                {cat.categoryName} ({cat.words.length} términos)
              </h4>

              {/* FICHAS DE PALABRAS Y EXPRESIONES */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cat.words.map((item, wIdx) => (
                  <div key={wIdx} className="bg-slate-950 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-lg flex flex-col justify-between hover:border-emerald-500/40 transition">
                    
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <h5 className="text-lg font-black text-white">
                          <InteractiveText text={item.term} onWordClick={handleWordClick} />
                        </h5>
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-400 bg-emerald-950 px-2.5 py-0.5 rounded-md border border-emerald-500/30">
                          {catIdx + 1}.{wIdx + 1}
                        </span>
                      </div>

                      {/* TRADUCCIÓN / SIGNIFICADO */}
                      <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 space-y-0.5">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Significado en Español:</span>
                        <p className="text-sm font-bold text-emerald-400">{item.spanish}</p>
                      </div>

                      {/* EJEMPLOS EN CONTEXTO (2 ORACIONES BILINGÜES) */}
                      <div className="space-y-2 pt-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">💬 Ejemplos en Contexto Cotidiano:</span>
                        <div className="space-y-2">
                          {item.examples.map((ex, exIdx) => (
                            <div key={exIdx} className="bg-slate-900 p-3 rounded-xl border border-slate-800/80 space-y-1 text-xs">
                              <p className="font-bold text-slate-100 flex items-center gap-1.5">
                                <span className="text-blue-400 shrink-0">🇺🇸</span>
                                <InteractiveText text={ex.en} onWordClick={handleWordClick} />
                              </p>
                              <p className="text-slate-400 flex items-center gap-1.5 italic text-[11px]">
                                <span className="text-emerald-400 shrink-0">🇨🇴</span> {ex.es}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* TIPS DE USO (PRONUNCIACIÓN / ERROR COMÚN) */}
                    {item.tip && (
                      <div className="bg-amber-950/60 border border-amber-500/30 p-3 rounded-xl text-xs space-y-0.5 mt-2">
                        <strong className="text-amber-300 text-[10px] font-extrabold uppercase tracking-wider block">Tip de Uso & Pronunciación:</strong>
                        <p className="text-amber-100/90 leading-relaxed font-medium">
                          {item.tip}
                        </p>
                      </div>
                    )}

                  </div>
                ))}
              </div>

            </div>
          ))}
        </div>

      </div>

      {/* EMERGENTE MODAL DE TRADUCCIÓN (SIN AUDIO) */}
      {activeWordData && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-slate-900 border border-emerald-500/50 rounded-3xl max-w-sm w-full p-6 relative shadow-2xl space-y-4 text-left">
            <button 
              onClick={() => setActiveWordData(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white font-bold text-lg bg-slate-800 w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer"
            >
              ✕
            </button>

            <div className="border-b border-slate-800 pb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 bg-emerald-950 px-2 py-0.5 rounded-full border border-emerald-500/30">
                {activeWordData.pos || 'Vocabulario'}
              </span>
              <h4 className="text-xl font-black text-white mt-1 capitalize">
                {activeWordData.word}
              </h4>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Traducción en Español:</span>
              <p className="text-base font-bold text-emerald-400">{activeWordData.trans}</p>
            </div>

            <button
              onClick={() => setActiveWordData(null)}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 rounded-xl transition text-xs shadow-lg cursor-pointer"
            >
              Entendido 👍
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
